import * as Yup from 'yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { usePanel } from 'hooks/usePanel';
import { BaseSection } from 'components/layout/Section/BaseSection';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import Loading from 'components/ui/Loader';
import { BankAccountDto } from 'api/bank-account';
import { BankAccount } from 'components/domain/Bank/BankAccount';
import { BankAccountCreateDialog } from 'components/domain/Bank/BankAccountCreateDialog';
import { GenericMessage } from 'components/feedback/Message';
import { Button } from 'components/ui/Button';
import { bankAccountsAPI } from 'services/swagger-api';
import {
  useQuoteContractualizationData,
  useIsSendingContract,
  useContractualizationActions
} from '../../store';

const validationSchema = Yup.object().shape({
  fullName: Yup.string().required('Le nom complet est requis'),
  email: Yup.string().email('Email invalide').required("L'email est requis")
});

interface FormValues {
  fullName: string;
  email: string;
}

interface ContractSignatureRequestProps {
  onSuccess?: () => void;
}

export const ContractSignatureRequest: React.FC<
  ContractSignatureRequestProps
> = ({ onSuccess }) => {
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const quoteData = useQuoteContractualizationData();
  const isSendingContract = useIsSendingContract();
  const { sendContractSignatureRequest } = useContractualizationActions();

  const { closePanel, setPanelActions } = usePanel();
  const [isFormValid, setIsFormValid] = useState(false);
  const { t } = useDynamicTranslation([NsKey.Contractualization]);

  const [bankAccounts, setBankAccounts] = useState<BankAccountDto[]>([]);
  const [isLoadingBankAccounts, setIsLoadingBankAccounts] = useState(true);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string | undefined>();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const loadBankAccounts = useCallback(async () => {
    try {
      setIsLoadingBankAccounts(true);
      const response = await bankAccountsAPI.getMyBankAccounts();
      if (response.isSuccess) {
        setBankAccounts(response.bankAccounts);
        const defaultAccount = response.bankAccounts.find(a => a.isDefault);
        if (defaultAccount) {
          setSelectedBankAccountId(defaultAccount.id);
        } else if (response.bankAccounts.length > 0) {
          setSelectedBankAccountId(response.bankAccounts[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading bank accounts:', err);
    } finally {
      setIsLoadingBankAccounts(false);
    }
  }, []);

  useEffect(() => {
    loadBankAccounts();
  }, [loadBankAccounts]);

  const hasBankAccount = bankAccounts.length > 0;
  const canSubmit = isFormValid && !isSendingContract && hasBankAccount && !!selectedBankAccountId && !isLoadingBankAccounts;

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: 'COMMON.CANCEL',
        submitLabel: 'COMMON.VALIDATE',
        submitDisabled: !canSubmit,
        onSubmit: () => {
          formikRef.current?.submitForm();
        }
      });
    }
  }, [formikRef, setPanelActions, canSubmit]);

  const handleSelectBankAccount = useCallback((bankAccount: BankAccountDto) => {
    setSelectedBankAccountId(bankAccount.id);
  }, []);

  if (!quoteData) {
    return <Loading isFullPage loading={true} />;
  }

  const { insured } = quoteData;

  const handleSubmit = async (values: FormValues) => {
    if (!quoteData.quoteId || !selectedBankAccountId) return;
    const result = await sendContractSignatureRequest({
      quoteId: quoteData.quoteId,
      fullName: values.fullName,
      email: values.email,
      hasGroupContract: !!quoteData.groupContractIdentifier,
      bankAccountId: selectedBankAccountId
    });

    if (result.success && quoteData.riskId) {
      onSuccess?.();
    }

    closePanel();
  };

  return (
    <>
      <Formik
        initialValues={{
          fullName: insured.displayName,
          email: insured.email
        }}
        validationSchema={validationSchema}
        innerRef={formikRef}
        onSubmit={handleSubmit}
      >
        {({ isValid }) => {
          if (isValid !== isFormValid) {
            setIsFormValid(isValid);
          }
          return (
            <Form>
              <BaseSection title={t('CONTRACTUALIZATION.LABELS.RECIPIENTS_INFOS')}>
                <>
                  <div className="col-span-6">
                    <FormikInputText
                      label="Nom complet"
                      required
                      type="text"
                      name="fullName"
                      placeholder="Veuillez saisir votre nom"
                      disabled={isSendingContract}
                    />
                  </div>
                  <div className="col-span-6 mt-2">
                    <FormikInputText
                      label="Email"
                      required
                      type="email"
                      name="email"
                      placeholder="Veuillez saisir votre email"
                      disabled={isSendingContract}
                    />
                  </div>
                </>
              </BaseSection>

              <div className="mt-6">
              <BaseSection title="Votre IBAN pour le versement des commissions">
                <>
                  {isLoadingBankAccounts ? (
                    <div className="col-span-6 flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-4 border-slate-200 border-t-primary" />
                    </div>
                  ) : (
                    <div className="col-span-6 flex flex-col gap-3">
                      {!hasBankAccount && (
                        <GenericMessage
                          type="warning"
                          message="Aucun compte bancaire enregistré. Veuillez en ajouter un avant d'envoyer une demande de signature."
                          dismissible={false}
                        />
                      )}
                      {bankAccounts.map(account => (
                        <BankAccount
                          key={account.id}
                          bankAccount={account}
                          isSelected={account.id === selectedBankAccountId}
                          onSelect={handleSelectBankAccount}
                        />
                      ))}
                      <Button
                        outline
                        color="primary"
                        onClick={() => setShowCreateDialog(true)}
                      >
                        Ajouter un compte bancaire
                      </Button>
                    </div>
                  )}
                </>
              </BaseSection>
              </div>
            </Form>
          );
        }}
      </Formik>

      <BankAccountCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={loadBankAccounts}
        isFirstAccount={bankAccounts.length === 0}
      />
    </>
  );
};
